// Derives the delivery state shown under a message bubble from the two places
// it can live, which is the whole point of this module.
//
// 1. `_status`, set optimistically by the batch send while the request is in
//    flight and immediately after it answers. Client-only: it exists on the
//    message object this tab created and nowhere else.
// 2. `metadata.delivery_failed`, written by the backend when it logs a failed
//    image. This is real server data, so it is what survives a refetch, a tab
//    reload, and arriving in another tab via WS.
//
// Reading only (1) was a real bug: refreshThreadAndList() runs after every
// send, invalidating the thread query, so the server's copy of the message
// replaces the optimistic one. The image stayed, but the error marker and the
// Retry button vanished a moment after appearing, which reads as "it worked".
//
// (1) wins while present because it is strictly fresher: during a retry it
// says 'sending' while the server still has delivery_failed from the previous
// attempt, and showing a stale failure under a live spinner would be wrong.
export function deriveDeliveryState(message) {
  if (!message) return null

  const status = message._status
  if (status === 'sending') return { kind: 'sending' }

  // An explicit 'sent' wins over the server's stale flag. retrySend patches
  // _status but cannot edit metadata, so between a successful retry and the
  // refetch that follows it the message still carries delivery_failed from the
  // previous attempt. Without this the row would stay red right after a retry
  // the staff member just watched succeed.
  if (status === 'sent') return null

  const serverFailed = message.metadata?.delivery_failed === true

  if (status === 'failed') {
    return {
      kind: 'failed',
      errorCode: message._errorCode ?? message.metadata?.delivery_error ?? null,
      errorDetail: message._errorDetail ?? null,
      // An optimistic failure carries its own retriability from the response.
      retriable: message._retriable !== false,
      retry: buildRetryTarget(message),
    }
  }

  // No optimistic status: this is the server's own record of the message,
  // which is the post-refetch and post-reload case.
  if (serverFailed) {
    return {
      kind: 'failed',
      errorCode: message.metadata?.delivery_error ?? null,
      errorDetail: message.metadata?.delivery_error_detail ?? null,
      // The server does not tell us whether a logged failure is retriable, so
      // allow a retry whenever there is something to resend. A retry that the
      // channel refuses again fails visibly, which beats offering no way out.
      retriable: true,
      retry: buildRetryTarget(message),
    }
  }

  // `_status === 'sent'`, or an ordinary message that predates all of this.
  return null
}

// Whether the bubble itself should render as failed. Derived from the same
// state as the status row below it, so a red border can never appear without
// its explanation, or linger after the explanation clears.
export function isDeliveryFailed(message) {
  return deriveDeliveryState(message)?.kind === 'failed'
}

// Everything retrySend needs to resend one image, from whichever shape of
// message is at hand. The optimistic `_retry` is preferred because it carries
// the uploaded size, letting the backend reject an oversized image without
// fetching it. Falling back to image_url is what makes a retry possible at all
// once the optimistic copy is gone.
//
// `clientRef` only has to correlate the request with its own response item, so
// a server id is a fine choice and a generated one works when there is no id.
export function buildRetryTarget(message) {
  if (message?._retry?.url) return message._retry

  const url = message?.image_url
  if (!url) return null

  const clientRef = message.message_id || message._localId || null
  return { kind: 'image', url, size: undefined, clientRef }
}
